import React from "react";

import instance from "@store/middleware/api";

import { colors, fonts, fontSizes } from "@/styles/design-system";

/**
 * Dev-only login switcher — flips the session between two known test
 * users without manual logout/login. Gated behind NODE_ENV !== "production".
 *
 * Test-account passwords come from REACT_APP_DEV_FREE_PASSWORD /
 * REACT_APP_DEV_SUB_PASSWORD (local .env only) — NEVER hardcoded, so no
 * plaintext credential ships in the JS bundle (2026-07-02 audit). If the vars
 * are unset the switcher simply won't authenticate.
 *
 * Sibling to `DevAccountSwitcher` (which toggles the *display* role —
 * Free / Subscription / Creator — but not the actual logged-in user).
 * Rendered in `SecondHeader.tsx` right after `DevAccountSwitcher`.
 */
const DEV_TEST_ACCOUNTS = [
  { key: "free", label: "Free", email: "free-test-2026@example.com", password: process.env.REACT_APP_DEV_FREE_PASSWORD || "" },
  { key: "subscription", label: "Subscription", email: "hoyaw23479@seosnaps.com", password: process.env.REACT_APP_DEV_SUB_PASSWORD || "" },
] as const;

const DEV_ACTIVE_ACCOUNT_KEY = "dev-active-test-account";

const isDev = process.env.NODE_ENV !== "production";

const DevUserSwitcher: React.FC = () => {
  if (!isDev) return null;

  const currentKey = typeof window !== "undefined"
    ? localStorage.getItem(DEV_ACTIVE_ACCOUNT_KEY)
    : null;
  const [switching, setSwitching] = React.useState<string | null>(null);

  const switchTo = async (acct: typeof DEV_TEST_ACCOUNTS[number]) => {
    setSwitching(acct.key);
    try {
      const { data } = await instance.post("/auth/login", { email: acct.email, password: acct.password });
      const now = Date.now();
      localStorage.setItem("accessToken", JSON.stringify({ value: data.accessToken, expiry: now + data.accessTokenTtl * 1000 }));
      localStorage.setItem("refreshToken", JSON.stringify({ value: data.refreshToken, expiry: now + data.refreshTokenTtl * 1000 }));
      localStorage.setItem(DEV_ACTIVE_ACCOUNT_KEY, acct.key);
      window.location.reload();
    } catch (e) {
      setSwitching(null);
      console.error(e);
    }
  };

  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "4px 8px", borderRadius: 8,
      background: "rgba(255, 255, 255, 0.03)", border: "1px dashed rgba(255, 255, 255, 0.15)",
      fontFamily: fonts.body, fontSize: fontSizes.xs, color: "rgba(251, 244, 236, 0.5)",
    }}>
      <span>DEV user</span>
      {DEV_TEST_ACCOUNTS.map((acct) => {
        const active = currentKey === acct.key;
        const pending = switching === acct.key;
        return (
          <button
            key={acct.key}
            onClick={() => !active && !pending && switchTo(acct)}
            disabled={active || pending}
            style={{
              padding: "3px 10px", borderRadius: 6, border: "none",
              cursor: active || pending ? "default" : "pointer",
              fontFamily: fonts.body, fontSize: fontSizes.xs,
              background: active ? "rgba(156, 211, 253, 0.2)" : "rgba(255, 255, 255, 0.06)",
              color: active ? colors.accent.blue : "rgba(251, 244, 236, 0.5)",
            }}
          >{pending ? "…" : acct.label}</button>
        );
      })}
    </div>
  );
};

export default DevUserSwitcher;
